import { type ComponentProps, useEffect, useState } from 'react';
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { DetailedVehicle } from '../../../../data/vehicles/useDetailedVehicle';
import useDetailedVehicle from '../../../../data/vehicles/useDetailedVehicle';
import InputText from '../../../../styled-components/components/InputText';
import { getMarketCode } from '../../../../utils/market';
import { getRegVinLookupFailureState } from './utils';
import { registrationOrVinNumber } from './validationMethods';

interface RegVinInputProps<T extends FieldValues> {
  setValue?: (vehicle?: DetailedVehicle) => void;
  control: Control<T>;
  names: {
    vehicleRegistration: Path<T>;
    hasDetails: Path<T>;
  };
  onValidate: () => void;
  onLookupFailure?: (error: boolean) => void;
  lookupFailureState?: ComponentProps<typeof InputText>['state'];
  disabled?: boolean;
}

export const RegVinInput = <T extends FieldValues>({
  setValue = () => {},
  control,
  names: { hasDetails, vehicleRegistration },
  onValidate,
  onLookupFailure,
  lookupFailureState,
  disabled,
}: RegVinInputProps<T>) => {
  const { t } = useTranslation(['createVehiclePanel', 'validation']);
  const marketCode = getMarketCode();
  const [validRegistration, setValidRegistration] = useState<string>();
  const [detailedVehicle, detailedVehicleQuery] = useDetailedVehicle(validRegistration);

  const { field: hasDetailsField, fieldState: hasDetailsFieldState } = useController({
    control,
    name: hasDetails,
  });

  const { field: registrationField, fieldState: registrationFieldState } = useController({
    control,
    name: vehicleRegistration,
  });

  useEffect(() => {
    const parsedRegistration = registrationOrVinNumber.safeParse({
      marketCode,
      registrationNumber: registrationField.value,
    });
    setValidRegistration(
      parsedRegistration.success ? parsedRegistration.data.registrationNumber : undefined,
    );
  }, [registrationField.value]);

  useEffect(() => {
    hasDetailsField.onChange(!!detailedVehicle);
    setValue(detailedVehicle);
    if (registrationFieldState.isTouched) {
      onValidate();
    }
  }, [detailedVehicle]);

  useEffect(() => {
    if (onLookupFailure) {
      onLookupFailure(detailedVehicleQuery.isError);
    }
  }, [detailedVehicleQuery.isError]);

  const loading = detailedVehicleQuery.isLoading;

  return (
    <InputText
      label={t('registrationNumberLabel', { ns: 'createVehiclePanel' })}
      error={
        t(registrationFieldState.error?.message ?? '', { ns: 'validation' }) ||
        t(hasDetailsFieldState.error?.message ?? '', { ns: 'createVehiclePanel' })
      }
      state={
        loading
          ? {
              type: 'loading',
            }
          : getRegVinLookupFailureState(
              lookupFailureState,
              !!detailedVehicleQuery.error,
              detailedVehicle,
            )
      }
      {...registrationField}
      onChange={(e) => {
        const trimmedValue = e.target.value.replace(/\s+/g, '').trim();
        registrationField.onChange(trimmedValue);
      }}
      disabled={disabled}
    />
  );
};
